import type { MediaManagerTemplate } from 'modules/manager/domain/interfaces';
import { TemplateType } from 'modules/manager/domain/template-type';
import { Pages } from 'modules/navigation/domain/enums/pages';

/**
 * Builds the headless render link for the template open in the editor, filled in with fixture
 * ids. Backs the local-only «Рендер» header button — nothing in the product builds render links,
 * external callers assemble them by hand from `docs/media-manager-render-links.md`, so this only
 * exists to save a developer that assembly while working on a template.
 *
 * The ids below are rows on the TEST database, hardcoded on purpose: the button is gated behind
 * `useIsLocal` (see editor-header.tsx), which is a compile-time constant in any built bundle, so
 * the button cannot render on a deployed environment no matter whose browser loads it. Treat the
 * ids as throwaway fixtures — nothing here is a contract, retarget them freely.
 */

const FIXTURE_STAGE_ID = 1649;
const FIXTURE_CHAMPIONSHIP_ID = 1677;
const FIXTURE_MATCH_IDS = [62651, 62652, 62653];
/** Global presets carry no tournament — fall back to a fixture id for the local render link. */
const FIXTURE_TOURNAMENT_ID = 3;

/**
 * Positional per layout-block region — `bgUrls[i]` fills region `i`, extras past the template's
 * region count are ignored by `applyLayoutBackgrounds`. Three covers every current preset.
 */
const FIXTURE_BG_URLS = [
  'https://images.unsplash.com/photo-1551958219-acbc608c6377?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1517649763962-0c623066013b?fm=jpg&q=60&w=3000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?fm=jpg&q=60&w=3000&auto=format&fit=crop',
];

/**
 * One builder per type — a `Record<TemplateType, …>` so adding a template type fails to compile
 * until its render link is defined here too.
 */
const LINK_BUILDERS: Record<TemplateType, (template: MediaManagerTemplate) => string> = {
  [TemplateType.SCHEDULE]: (template) => singleMatchLink(template),
  [TemplateType.RESULTS]: (template) => singleMatchLink(template),
  [TemplateType.MULTIPLE_SCHEDULE]: (template) => multiMatchLink(template),
  [TemplateType.MULTIPLE_RESULTS]: (template) => multiMatchLink(template),
  [TemplateType.TOURNAMENT_TABLE]: (template) =>
    Pages.MEDIA_MANAGER.builders.renderStandings({
      stageId: FIXTURE_STAGE_ID,
      templateId: template.id,
      bgUrls: FIXTURE_BG_URLS,
    }),
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: (template) =>
    Pages.MEDIA_MANAGER.builders.statRender({
      championshipId: FIXTURE_CHAMPIONSHIP_ID,
      templateId: template.id,
      bgUrls: FIXTURE_BG_URLS,
    }),
};

function singleMatchLink(template: MediaManagerTemplate): string {
  return Pages.MEDIA_MANAGER.builders.render({
    tournamentId: template.tournamentId ?? FIXTURE_TOURNAMENT_ID,
    championshipId: FIXTURE_CHAMPIONSHIP_ID,
    matchId: FIXTURE_MATCH_IDS[0],
    templateId: template.id,
    bgUrls: FIXTURE_BG_URLS,
  });
}

function multiMatchLink(template: MediaManagerTemplate): string {
  return Pages.MEDIA_MANAGER.builders.renderMultiple({
    matchIds: FIXTURE_MATCH_IDS,
    templateId: template.id,
    bgUrls: FIXTURE_BG_URLS,
  });
}

export function buildDevRenderLink(template: MediaManagerTemplate): string {
  return LINK_BUILDERS[template.templateType](template);
}
